<script setup lang="ts" generic="T extends { name: string }">
import { computed, ref } from "vue";
import { PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";

import { RsCheckboxGroup } from "@/components";
import ConfirmPopover from "@/components/ConfirmPopover.vue";

withDefaults(defineProps<{
  excluded: T[];
  additional: T[];
  original: T[];
  canAdd: boolean;
  canRestore: boolean;
  canDelete: boolean;
  errorMessage?: string;
}>(), {  })

defineSlots<{
  columns(): void;
  row(props: T): void;
}>();

const emit = defineEmits<{
  (e: "addEntry", name: string): void;
  (e: "deleteEntry", name: string): void;
  (e: "restoreEntry", name: string): void;
}>();

const filterOptions = [
  { value: "showOriginal" as const, text: "Show Original" },
  { value: "showAdditional" as const, text: "Show Additional" },
  { value: "showExcluded" as const, text: "Show Excluded" },
];

const showFilters = ref(false);

const mutationLoading = ref<
  { type: "adding" } | { type: "restoring"; item: string } | { type: "deleting"; item: string }
>();

const filters = ref<Array<"Any" | (typeof filterOptions)[number]["value"]>>([
  "Any",
  ...filterOptions.map((opt) => opt.value),
]);

const resolved = computed(() => {
  return [...__props.original, ...__props.additional, ...__props.excluded].reduce<
    Array<{ data: T; inOriginal: boolean; state: "EXCLUDED" | "ADDITIONAL" | undefined }>
  >((acc, cur) => {
    if (acc.find((item) => item.data.name === cur.name) != null) return acc;

    const inOriginal = __props.original.find((item) => item.name === cur.name) != null;
    const state =
      __props.excluded.find((item) => item.name === cur.name) != null
        ? "EXCLUDED"
        : __props.additional.find((item) => item.name === cur.name) != null
          ? "ADDITIONAL"
          : undefined;

    if (
      (inOriginal && filters.value.includes("showOriginal")) ||
      (state === "EXCLUDED" && filters.value.includes("showExcluded")) ||
      (state === "ADDITIONAL" && filters.value.includes("showAdditional"))
    ) {
      return [...acc, { data: cur, inOriginal, state }];
    }

    return acc;
  }, []);
});

function onAddEntry(e: Event) {
  if (!__props.canAdd) return;

  const data = new FormData(e.target as HTMLFormElement);
  const name = (data.get("entry") as string | null)?.trim();

  if (name == null || name.length < 1) return;

  mutationLoading.value = { type: "adding" };
  emit("addEntry", name);
}

function onRestoreEntry(item: (typeof resolved)["value"][number]) {
  if (!__props.canRestore || item.state !== "EXCLUDED") return;

  mutationLoading.value = { type: "restoring", item: item.data.name.trim() };
  emit("restoreEntry", item.data.name);
}

function onDeleteEntry(item: (typeof resolved)["value"][number]) {
  if (!__props.canDelete || item.state === "EXCLUDED") return;

  mutationLoading.value = { type: "deleting", item: item.data.name.trim() };
  emit("deleteEntry", item.data.name);
}
</script>
<template>
  <div class="tw-size-full tw-space-y-4 tw-overflow-auto tw-px-2 tw-py-4">
    <div class="tw-flex tw-items-center tw-gap-2">
      <PopoverRoot v-model:open="showFilters" modal>
        <PopoverTrigger class="tw-btn-lg-gray" :aria-label="showFilters ? 'Hide Filters' : 'Show Filters'">
          {{ showFilters ? "Hide Filters" : "Show Filters" }}
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent side="bottom" align="start" :align-offset="3" class="tw-z-[1000000]">
            <RsCheckboxGroup
              v-model="filters"
              stacked
              class="tw-b-r tw-flex tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-max-w-xs tw-flex-col tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-2 tw-shadow"
              :options="filterOptions"
            />
            <PopoverArrow class="tw-fill-white" />
          </PopoverContent>
        </PopoverPortal>
      </PopoverRoot>
      <div class="tw-flex-1" />
      <span v-if="errorMessage != null" class="tw-text-red-700">
        {{ errorMessage }}
      </span>
      <PopoverRoot v-if="canAdd">
        <PopoverTrigger
          class="tw-btn-lg-gray invert tw-flex tw-min-w-max tw-items-center tw-gap-2 tw-rounded-md tw-font-medium"
          :disabled="mutationLoading?.type === 'adding'"
        >
          <i aria-hidden="true" class="fas fa-plus" />
          Add Entry
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent as-child align="end" :align-offset="1.5">
            <form
              class="tw-b-r tw-z-[1000000] tw-flex tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-w-60 tw-flex-col tw-gap-2 tw-overflow-y-auto tw-rounded-lg tw-border-[#636463]/20 tw-bg-white tw-p-2 tw-shadow"
              @submit.prevent="onAddEntry"
            >
              <input
                type="text"
                name="entry"
                placeholder="Entry name"
                class="tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
              />
              <div class="tw-flex tw-items-center tw-justify-end">
                <button type="submit" class="tw-btn-md-green !tw-px-4">
                  <i v-if="mutationLoading?.type === 'adding'" aria-hidden="true" class="fas fa-spin fa-circle-notch" />
                  Add
                </button>
              </div>
              <PopoverArrow class="tw-fill-white" />
            </form>
          </PopoverContent>
        </PopoverPortal>
      </PopoverRoot>
    </div>
    <div class="tw-w-full tw-overflow-x-auto">
      <table class="tw-min-w-full tw-table-fixed tw-self-start tw-border">
        <thead>
          <tr class="tw-h-9 tw-divide-x tw-border-b [&>*]:tw-bg-gray-100 [&>*]:tw-p-2">
            <th class="tw-w-20">#</th>
            <th>Name</th>
            <slot name="columns" />
            <th v-if="canRestore || canDelete" class="tw-w-40">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, idx) in resolved"
            :key="item.data.name"
            class="tw-h-7 tw-cursor-pointer tw-divide-x tw-border-b last:tw-border-b-0 [&>*]:tw-p-2 [&>*]:hover:tw-bg-gray-300 [&>*]:focus:tw-bg-gray-300"
          >
            <td>{{ idx + 1 }}.</td>
            <td>
              <div class="tw-space-x-2">
                {{ item.data.name }}
                <span
                  v-if="item.inOriginal"
                  class="flex tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-500/20 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-blue-900"
                >
                  Original
                </span>
                <span
                  v-if="item.state != null"
                  class="flex tw-items-center tw-justify-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium"
                  :class="
                    item.state === 'EXCLUDED'
                      ? 'tw-bg-red-500/20 tw-text-red-900'
                      : 'tw-bg-green-500/20 tw-text-green-900'
                  "
                >
                  {{ item.state === "EXCLUDED" ? "Excluded" : "Additional" }}
                </span>
              </div>
            </td>
            <slot name="row" :="item.data" />
            <td v-if="item.state === 'EXCLUDED' ? canRestore : canDelete">
              <ConfirmPopover
                v-if="item.state === 'EXCLUDED'"
                confirm-text="Yes, I'm sure"
                :trigger-props="{ asChild: true }"
                :is-loading="mutationLoading?.type === 'restoring' && mutationLoading.item === item.data.name"
                @confirmed="() => onRestoreEntry(item)"
              >
                <template #trigger>
                  <button type="button" class="tw-btn-md-gray tw-flex tw-min-w-max tw-items-center tw-gap-2">
                    <i aria-hidden="true" class="fas fa-arrow-rotate-left" />
                    Restore
                  </button>
                </template>
                <template #question>Are you sure you want to restore this entry?</template>
              </ConfirmPopover>
              <ConfirmPopover
                v-else
                confirm-text="Yes, I'm sure"
                :trigger-props="{ asChild: true }"
                :is-loading="mutationLoading?.type === 'deleting' && mutationLoading.item === item.data.name"
                @confirmed="() => onDeleteEntry(item)"
              >
                <template #trigger>
                  <button type="button" class="tw-btn-md-gray tw-flex tw-min-w-max tw-items-center tw-gap-2">
                    <template v-if="item.state == null">
                      <i aria-hidden="true" class="fas fa-xmark" />
                      Exclude
                    </template>
                    <template v-else>
                      <i aria-hidden="true" class="fas fa-trash" />
                      Delete
                    </template>
                  </button>
                </template>
                <template #question>
                  <template v-if="item.state == null">Are you sure you want to exclude this entry?</template>
                  <template v-else>Are you sure you want to delete this additional entry?</template>
                </template>
              </ConfirmPopover>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="resolved.length < 1" class="tw-w-full tw-text-center tw-font-medium">No data to show</div>
  </div>
</template>
